import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, Undo2, Eraser, Save as SaveIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_DIM = 1200;
const STROKE_COLOR = '#EF4444';

/**
 * SitePhotoAnnotator — Alian: "las fotos que tiene que upload... site photos,
 * y que pueda hacerle un circulito o alguna notificacion a la foto para
 * subirla." Lets the user pick/take a photo, draw a freehand mark (circle,
 * arrow, underline, etc.) directly on top of it with a finger/mouse, add an
 * optional note, and upload the flattened (photo + markup) image to
 * /files/upload under the given category — no extra navigation needed.
 */
export default function SitePhotoAnnotator({ entityType, entityId, category = 'site_photos', onUploaded }) {
  const [open, setOpen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [hasStrokes, setHasStrokes] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const retakeInputRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const strokesRef = useRef([]);
  const drawingRef = useRef(false);

  const resetState = () => {
    setImgLoaded(false);
    setNote('');
    setHasStrokes(false);
    setUploadPct(0);
    strokesRef.current = [];
    imgRef.current = null;
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) resetState();
  };

  const drawAll = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    strokesRef.current.forEach(stroke => {
      if (stroke.points.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = Math.max(3, canvas.width / 150);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      stroke.points.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    });
  };

  useEffect(() => {
    if (!imgLoaded || !imgRef.current || !canvasRef.current) return;
    const img = imgRef.current;
    const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
    canvasRef.current.width = Math.round(img.width * scale);
    canvasRef.current.height = Math.round(img.height * scale);
    drawAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoVersion]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        imgRef.current = img;
        strokesRef.current = [];
        setHasStrokes(false);
        setImgLoaded(true);
        setPhotoVersion(v => v + 1);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    strokesRef.current.push({ points: [getPos(e)], color: STROKE_COLOR });
  };
  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    stroke.points.push(getPos(e));
    drawAll();
  };
  const handlePointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    setHasStrokes(strokesRef.current.length > 0);
  };

  const handleUndo = () => {
    strokesRef.current.pop();
    drawAll();
    setHasStrokes(strokesRef.current.length > 0);
  };
  const handleClear = () => {
    strokesRef.current = [];
    drawAll();
    setHasStrokes(false);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    setUploadPct(0);
    canvas.toBlob(async (blob) => {
      if (!blob) { setSaving(false); toast.error('Error preparando la foto'); return; }
      try {
        const formData = new FormData();
        formData.append('file', blob, `site_photo_${Date.now()}.jpg`);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId);
        formData.append('doc_type', 'photo');
        formData.append('category', category);
        if (note.trim()) formData.append('description', note.trim());
        // Progreso de subida — importante en el campo con señal débil
        // (Alian: "para hacer el cutting log y poner fotos... conexión de
        // campo puede ser lenta").
        await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
          },
        });
        toast.success('Foto guardada');
        handleOpenChange(false);
        if (onUploaded) onUploaded();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error al subir la foto');
      } finally {
        setSaving(false);
      }
    }, 'image/jpeg', 0.9);
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-9 sm:h-8 text-xs border-[#003366]/20 text-[#003366]"
        data-testid="site-photo-open-btn"
      >
        <Camera className="w-3.5 h-3.5 mr-1.5" /> Site Photo
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg" data-testid="site-photo-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#003366]" /> Site Photo
            </DialogTitle>
          </DialogHeader>

          {!imgLoaded ? (
            <div className="py-8 text-center">
              <Camera className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-xs text-gray-400 mb-4">Toma o selecciona una foto del sitio para marcarla.</p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Button onClick={() => cameraInputRef.current?.click()} className="h-11 bg-[#003366] hover:bg-[#004080] text-white" data-testid="site-photo-camera-btn">
                  <Camera className="w-4 h-4 mr-1.5" /> Tomar foto
                </Button>
                <Button onClick={() => galleryInputRef.current?.click()} variant="outline" className="h-11" data-testid="site-photo-gallery-btn">
                  Elegir de galería
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-[11px] text-gray-400">Dibuja un círculo o marca sobre la foto para señalar algo (con el dedo o el mouse).</p>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                <canvas
                  ref={canvasRef}
                  className="max-w-full cursor-crosshair"
                  style={{ touchAction: 'none' }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                  data-testid="site-photo-canvas"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button type="button" variant="outline" size="sm" className="h-10" onClick={handleUndo} disabled={!hasStrokes} data-testid="site-photo-undo-btn">
                  <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-10" onClick={handleClear} disabled={!hasStrokes} data-testid="site-photo-clear-btn">
                  <Eraser className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-10" onClick={() => retakeInputRef.current?.click()} data-testid="site-photo-retake-btn">
                  Otra foto
                </Button>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-500 uppercase">Nota (opcional)</Label>
                <Input
                  className="h-11"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Ej: Fuga en la válvula principal"
                  data-testid="site-photo-note-input"
                />
              </div>
              {saving && (
                <div className="space-y-1" data-testid="site-photo-upload-progress">
                  <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#003366] transition-all duration-200" style={{ width: `${uploadPct}%` }} />
                  </div>
                  <p className="text-[10px] text-gray-400 text-right">{uploadPct}%</p>
                </div>
              )}
            </div>
          )}

          {/* Cámara directa (capture=environment) y galería — dos inputs
              separados para que el navegador ofrezca ambas rutas en el
              celular (Alian: "que pueda tomar foto con la cámara
              directamente además de elegir de la galería"). */}
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} data-testid="site-photo-camera-input" />
          <input ref={galleryInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} data-testid="site-photo-gallery-input" />
          <input ref={retakeInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} data-testid="site-photo-retake-input" />

          <DialogFooter>
            <Button variant="outline" className="h-11 sm:h-9" onClick={() => handleOpenChange(false)} data-testid="site-photo-cancel-btn">Cancelar</Button>
            <Button
              className="h-11 sm:h-9 bg-[#003366] hover:bg-[#004080] text-white"
              onClick={handleSave}
              disabled={!imgLoaded || saving}
              data-testid="site-photo-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <SaveIcon className="w-4 h-4 mr-1.5" />}
              {saving ? `Subiendo… ${uploadPct}%` : 'Guardar foto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
